"use client";

import { useRef, useState } from "react";
import { z } from "zod/v4";
import { Check, MoveRight } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { HoneypotField } from "@/components/marketing/honeypot-field";
import { HONEYPOT_FIELD, ELAPSED_FIELD } from "@/lib/form-fields";

/* ------------------------------------------------------------------ */
/*  Validation schema                                                 */
/* ------------------------------------------------------------------ */

const contactSchema = z.object({
  name: z.string().min(2, "Please enter your name"),
  email: z.email("Please enter a valid email address"),
  company: z.string().optional(),
  message: z.string().min(10, "Message must be at least 10 characters"),
});

type Status = "idle" | "loading" | "error";

/* ------------------------------------------------------------------ */
/*  Contact form                                                      */
/* ------------------------------------------------------------------ */

export default function ContactForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<Status>("idle");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const startedAt = useRef(Date.now());

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitError(null);

    // Read the honeypot synchronously before any await (currentTarget resets).
    const honeypot = String(
      new FormData(e.currentTarget).get(HONEYPOT_FIELD) ?? "",
    );

    const result = contactSchema.safeParse({ name, email, company, message });

    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of result.error.issues) {
        const key = String(issue.path[0]);
        if (!fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }

    setErrors({});
    setStatus("loading");

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: result.data.name,
          email: result.data.email,
          message: result.data.message,
          ...(result.data.company ? { company: result.data.company } : {}),
          [HONEYPOT_FIELD]: honeypot,
          [ELAPSED_FIELD]: Date.now() - startedAt.current,
        }),
      });

      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as {
          error?: string;
        };
        throw new Error(
          data.error ?? "Something went wrong. Please try again.",
        );
      }

      setSubmitted(true);
    } catch (err) {
      setStatus("error");
      setSubmitError(
        err instanceof Error
          ? err.message
          : "Something went wrong. Please try again.",
      );
    }
  }

  /* ---- Success state ---- */

  if (submitted) {
    return (
      <div
        role="status"
        className="rounded-md border border-line bg-paper p-8 text-center"
      >
        <span className="mx-auto inline-flex size-12 items-center justify-center rounded-md border border-secondary/40 bg-secondary/[0.08]">
          <Check className="size-6 text-secondary" aria-hidden="true" />
        </span>
        <h2 className="mt-4 font-heading text-xl font-semibold text-dark">
          Message sent
        </h2>
        <p className="mt-2 text-mid">
          Thanks for getting in touch. We&rsquo;ll come back to you as soon as
          we can.
        </p>
      </div>
    );
  }

  /* ---- Form ---- */

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-6 rounded-md border border-line bg-paper p-6 sm:p-8"
      noValidate
    >
      <div className="grid gap-6 sm:grid-cols-2">
        <Input
          label="Your name"
          name="name"
          placeholder="Jane Smith"
          autoComplete="name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={errors.name}
        />
        <Input
          label="Email address"
          name="email"
          type="email"
          placeholder="jane@example.co.uk"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={errors.email}
        />
      </div>

      <Input
        label="Company"
        name="company"
        hint="Optional"
        placeholder="Where you work"
        autoComplete="organization"
        value={company}
        onChange={(e) => setCompany(e.target.value)}
        error={errors.company}
      />

      <div className="flex flex-col gap-1.5">
        <label htmlFor="message" className="text-sm font-semibold text-dark">
          Message
        </label>
        <textarea
          id="message"
          name="message"
          rows={6}
          required
          placeholder="Tell us how we can help…"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className={`min-h-[120px] w-full rounded-md border bg-paper px-4 py-2.5 text-base text-dark placeholder:text-mid/60 transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 ${
            errors.message
              ? "border-error focus-visible:outline-error"
              : "border-line hover:border-mid/50 focus-visible:outline-secondary"
          }`}
          aria-invalid={errors.message ? true : undefined}
          aria-describedby={errors.message ? "message-error" : undefined}
        />
        {errors.message && (
          <p
            id="message-error"
            role="alert"
            className="text-xs font-medium text-error"
          >
            {errors.message}
          </p>
        )}
      </div>

      {submitError && (
        <p role="alert" className="text-sm font-medium text-error">
          {submitError}
        </p>
      )}

      <Button
        type="submit"
        size="lg"
        className="w-full sm:w-auto"
        disabled={status === "loading"}
      >
        {status === "loading" ? "Sending…" : "Send message"}
        {status !== "loading" && (
          <MoveRight className="size-4" aria-hidden="true" />
        )}
      </Button>

      <HoneypotField />
    </form>
  );
}
